import { BrandMediaFile, BrandMediaType } from 'types'
import Styles from './BrandKitPreviewCard.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCreateBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useCreateBrandMediaFile'
import { Edit1, Upload5 } from 'blaze-ui/icons'
import { useState } from 'react'
import { useDeleteBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useDeleteBrandMediaFile'
import { toBase64 } from '../utils'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Spinner } from 'common/Spinner/Spinner'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'

interface Props {
  brandVisualKitId: number
  initialLogo?: BrandMediaFile<BrandMediaType> | null
  backgroundColor?: string
  isReadOnly?: boolean
  onLogoChange?: () => void
  className?: string
}

const Loading = () => (
  <div className={Styles.logoUploadContainer}>
    <Spinner />
  </div>
)

export const BrandKitLogo = ({
  brandVisualKitId,
  initialLogo,
  backgroundColor,
  isReadOnly,
  onLogoChange,
  className,
}: Props) => {
  const [logo, setLogo] = useState<BrandMediaFile<BrandMediaType> | null | undefined>(initialLogo)
  const [logoUrl, setLogoUrl] = useState<string | null | undefined>(initialLogo?.url)

  const { mutateAsync: createBrandMediaFile, isLoading: isCreatingLogo } = useCreateBrandMediaFile()
  const { mutateAsync: deleteLogo, isLoading: isDeletingLogo } = useDeleteBrandMediaFile()

  const { uploadImageFiles, isUploading } = useImportMediaFiles(false, async (files) => {
    files.forEach(async (file) => {
      if (file.media_file_id) {
        const newLogo = await createBrandMediaFile({
          brandVisualKitId,
          brandMediaType: BrandMediaType.LOGO,
          mediaFileId: file.media_file_id,
        })
        if (logo?.id) {
          await deleteLogo({
            id: logo.id,
            brandVisualKitId: brandVisualKitId,
          })
        }
        setLogo(newLogo.brand_media_file)
        onLogoChange?.()
      }
    })
  })

  const isLogoUploading = isDeletingLogo || isUploading || isCreatingLogo

  if (isLogoUploading)
    return (
      <div className={Styles.logoContainer}>
        <div className={Styles.logoUploadContainer}>
          <Loading />
        </div>
      </div>
    )

  if (logoUrl)
    return (
      <div className={cx(Styles.logoContainer, className)}>
        <div className={Styles.logoImage}>
          <img src={logoUrl} alt="logo" className={Styles.logo} style={{ backgroundColor }} />
          {!isReadOnly && (
            <div className={Styles.logoEditButton}>
              <FileTrigger
                accept={['image/*']}
                onSelect={async (files) => {
                  if (!files) return
                  const base64Url = (await toBase64(files[0])) as string

                  setLogoUrl(base64Url) // set new logo url without waiting for the upload to finish
                  await uploadImageFiles(Array.from(files))
                }}
              >
                <IconButton variant="secondary" size="xs" icon={Edit1} rounded />
              </FileTrigger>
            </div>
          )}
        </div>
      </div>
    )

  return (
    <div className={cx(Styles.logoContainer, className)}>
      <FileTrigger
        accept={['image/*']}
        onSelect={async (files) => {
          if (!files) return
          const base64Url = (await toBase64(files[0])) as string

          setLogoUrl(base64Url) // set new logo url without waiting for the upload to finish
          await uploadImageFiles(Array.from(files))
        }}
      >
        <div className={Styles.logoUploadContainer}>
          <Button variant="secondary" size="xs" className={Styles.logoUploadButton}>
            <Upload5 size={20} />
            <Text variant="label" color="var(--selected-fill)">
              Logo
            </Text>
          </Button>
        </div>
      </FileTrigger>
    </div>
  )
}
